import { Head, Link } from '@inertiajs/react';
import { useTrans } from '@/Utils/trans';

export default function Welcome({ auth }) {
    const __ = useTrans();
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            <Head title="Да, България 2.0" />
            
            {/* Header */}
            <header className="bg-white shadow">
                <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center">
                        <div className="flex-shrink-0">
                            <img 
                                className="h-12 w-auto" 
                                src="https://dabulgaria.bg/wp-content/themes/dabulgaria2/images/logo/dabulgaria.svg" 
                                alt="Да, България!"
                            />
                        </div>
                        <div className="hidden md:flex items-center gap-3">
                            {auth.user ? (
                                <Link
                                    href={route('dashboard')}
                                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                >
                                    {__('Dashboard')}
                                </Link>
                            ) : (
                                <>
                                    <Link
                                        href={route('login')}
                                        className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-gray-900"
                                    >
                                        Вход
                                    </Link>
                                    <Link
                                        href={route('register')}
                                        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                                    >
                                        Регистрация като симпатизант
                                    </Link>
                                    <Link
                                        href={route('register') + '?join=1'}
                                        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                                    >
                                        Стани член на Да, България
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <main>
                <div className="relative overflow-hidden">
                    <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                                <span className="block">{__('Welcome to')}</span>
                                <span className="block text-green-600 mt-2">{__('Yes, Bulgaria 2.0')}</span>
                            </h1>
                            <p className="mt-6 max-w-2xl mx-auto text-xl text-gray-600">
                                {__('Platform for civic participation, democratic initiatives and social change')}
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
                                {!auth.user ? (
                                    <>
                                        <Link
                                            href={route('login')}
                                            className="inline-flex items-center justify-center px-8 py-4 border-2 border-green-600 text-lg font-medium rounded-md text-green-600 bg-white hover:bg-green-50"
                                        >
                                            Вход
                                        </Link>
                                        <Link
                                            href={route('register')}
                                            className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 shadow-lg"
                                        >
                                            Регистрация като симпатизант
                                        </Link>
                                        <Link
                                            href={route('register') + '?join=1'}
                                            className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-lg"
                                        >
                                            Стани член на Да, България
                                        </Link>
                                    </>
                                ) : (
                                    <Link
                                        href={route('dashboard')}
                                        className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-md text-white bg-green-600 hover:bg-green-700 shadow-lg"
                                    >
                                        Към таблото →
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Features */}
                <div className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-base text-green-600 font-semibold tracking-wide uppercase">{__('Features')}</h2>
                            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl">
                                {__('How can you participate?')}
                            </p>
                        </div>

                        <div className="mt-12">
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Initiatives')}</h3>
                                    <p className="text-gray-600">{__('Propose and vote on public initiatives, legislative changes and civic campaigns.')}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Donations and Membership')}</h3>
                                    <p className="text-gray-600">{__('Support the cause with monthly or one-time donations. Become a member with voting rights.')}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Work Groups')}</h3>
                                    <p className="text-gray-600">{__('Join thematic work groups and participate actively in policy development.')}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Voting')}</h3>
                                    <p className="text-gray-600">{__('Participate in democratic decisions through voting on important issues and surveys.')}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Gamification')}</h3>
                                    <p className="text-gray-600">{__('Earn badges and points for your activity. Rank in the top 100 most active members.')}</p>
                                </div>

                                <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-500 text-white mb-4">
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{__('Local Organizations')}</h3>
                                    <p className="text-gray-600">{__('Connect with the local organization in your city and participate in local initiatives.')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white">
                <div className="max-w-7xl mx-auto py-12 px-4 overflow-hidden sm:px-6 lg:px-8">
                    <p className="mt-8 text-center text-base text-gray-400">
                        &copy; {new Date().getFullYear()} {__('Yes, Bulgaria')}. {__('All rights reserved.')}.
                    </p>
                </div>
            </footer>
        </div>
    );
}